import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { IoClose, IoMenu, IoChevronDown } from "react-icons/io5";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from 'react-i18next';
import "./NavbarHook.css";

const NavbarHook = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const isMobile = useMediaQuery({ query: "(max-width: 1080px)" });

  const { t, i18n } = useTranslation();

  const langLabels = {
    en: "English",
    hu: "Hungarian",
    de: "German",
  }

  const currentCode = (i18n.language || "en").split("-")[0];
  const currentLangLabel = langLabels[currentCode] || "English";

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  };

  const closeMobileMenu = () => {
    if (isMobile) {
      setIsMenuOpen(false);
      document.body.classList.remove('overflow-hidden');
    }
  };

  const toggleDropdown = (e) => {
    e?.stopPropagation?.();
    setIsDropdownOpen((v) => {
      const next = !v;
      if (next) setIsLangOpen(false);
      return next;
    });
  };

  const toggleLanguageDropdown = (e) => {
    e?.stopPropagation?.();
    setIsLangOpen((v) => {
      const next = !v;
      if (next) setIsDropdownOpen(false);
      return next;
    });
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsLangOpen(false);
  }

  const langRef = useRef(null);

  useEffect(() => {
    if (!isMobile || !isMenuOpen) return;
    const onClickAway = (e) => {
      if (!e.target.closest('.dropdown')) {
        setIsDropdownOpen(false);
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [isMobile, isMenuOpen]);

  const renderNavLinks = () => {
    const listClassName = isMobile ? "nav__list" : "nav__list__web";
    const linkClassName = "nav__link";
    const buttonClassName = "nav__cta";

    return (
      <ul className={listClassName}>
        <li>
          <NavLink to="/" className={linkClassName} onClick={closeMobileMenu}>
            {t('nav.home')}
          </NavLink>
        </li>
        <li>
          <NavLink
            to="/about-us"
            className={linkClassName}
            onClick={closeMobileMenu}
          >
            {t('nav.about')}
          </NavLink>
        </li>
        <li>
          <NavLink
            to="/adopt"
            className={linkClassName}
            onClick={closeMobileMenu}
          >
            {t('nav.adopt')}
          </NavLink>
        </li>
        <li>
          <NavLink
            to="/happy-stories"
            className={linkClassName}
            onClick={closeMobileMenu}
          >
            {t('nav.happyStories')}
          </NavLink>
        </li>
        <li>
          <NavLink
            to="/in-memoriam"
            className={linkClassName}
            onClick={closeMobileMenu}
          >
            {t('nav.inMemoriam')}
          </NavLink>
        </li>
        <li
          className="dropdown"
          onClick={isMobile ? toggleDropdown : undefined}
        >
          <div className={linkClassName}>
            {t('nav.getInvolved')}<IoChevronDown className="icon" />
          </div>
          <div
            className={`dropdown-content ${isDropdownOpen && isMobile ? "show-dropdown" : ""
              }`}
          >
            <NavLink
              to="/volunteer"
              onClick={closeMobileMenu}
              style={({ isActive }) => ({
                fontWeight: isActive ? 'bold' : 'normal',
                textDecoration: isActive ? 'underline' : 'none',
                color: isActive ? '#79ff72' : '#fff'
              })}
            >
              {t('nav.volunteer')}
            </NavLink>

            <NavLink
              to="/become-a-foster-home"
              onClick={closeMobileMenu}
              style={({ isActive }) => ({
                fontWeight: isActive ? 'bold' : 'normal',
                textDecoration: isActive ? 'underline' : 'none',
                color: isActive ? '#79ff72' : '#fff'
              })}
            >
              {t('nav.becomeFosterHome')}
            </NavLink>

          </div>
        </li>
        <li>
          <NavLink
            to="/contact"
            className={linkClassName}
            onClick={closeMobileMenu}
          >
            {t('nav.contact')}
          </NavLink>
        </li>
        <li>
          <NavLink
            to="/donate"
            className={linkClassName}
            onClick={closeMobileMenu}
          >
            {t('nav.donate')}
          </NavLink>
        </li>
        <li className="dropdown dropdown--lang" ref={langRef}>
          <button
            type="button"
            className={`${buttonClassName} nav__cta--lang`}
            onClick={toggleLanguageDropdown}
            aria-haspopup="true"
            aria-expanded={isLangOpen ? "true" : "false"}
          >
            {currentLangLabel} <IoChevronDown className="icon" />
          </button>
          <div className={`dropdown-content dropdown-content--lang ${isLangOpen ? "show-dropdown" : ""}`}>
            <button
              className="nav__link"
              onClick={() => changeLanguage('en')}
              style={{
                fontWeight: i18n.language === 'en' ? 'bold' : 'normal',
                textDecoration: i18n.language === 'en' ? 'underline' : 'none',
                color: i18n.language === 'en' ? '#79ff72' : '#fff'
              }}
            >
              English
            </button>

            <button
              className="nav__link"
              onClick={() => changeLanguage('hu')}
              style={{
                fontWeight: i18n.language === 'hu' ? 'bold' : 'normal',
                textDecoration: i18n.language === 'hu' ? 'underline' : 'none',
                color: i18n.language === 'hu' ? '#79ff72' : '#fff'
              }}
            >
              Hungarian
            </button>

            <button
              className="nav__link"
              onClick={() => changeLanguage('de')}
              style={{
                fontWeight: i18n.language === 'de' ? 'bold' : 'normal',
                textDecoration: i18n.language === 'de' ? 'underline' : 'none',
                color: i18n.language === 'de' ? '#79ff72' : '#fff'
              }}
            >
              German
            </button>
          </div>
        </li>
      </ul>
    );
  };

  return (
    <header className="header">
      <div className="topbar">
        <div className="logo">
          <NavLink to="/" className="nav__logo">
            <img
              src="../img/logo.jpg"
              alt="Navigation Logo"
            />
            <p className="nav__logo__title">{t('nav.logoTitle1')} <br /> {t('nav.logoTitle2')} </p>
          </NavLink></div>
        {isMobile && (
          <div className="nav__toggle" id="nav-toggle" onClick={toggleMenu}>
            <IoMenu style={{ color: 'white' }} />
          </div>
        )}
        {isMobile ? (
          <div
            className={`nav__menu  ${isMenuOpen ? "show-menu" : ""}`}
            id="nav-menu"
          >
            {renderNavLinks()}
            <div className="nav__close" id="nav-close" onClick={toggleMenu}>
              <IoClose style={{ color: 'white' }} />
            </div>
          </div>
        ) : (
          renderNavLinks()
        )}
      </div>
    </header>
  );
};

export default NavbarHook;
